function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

function firstNonEmptyString(values) {
  for (const value of values) {
    const normalizedValue = normalizeOptionalString(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return null;
}

const MEDIA_CONTENT_TYPES_BY_EXTENSION = Object.freeze({
  ".mp4": "video/mp4",
  ".m4v": "video/mp4",
  ".mov": "video/quicktime",
  ".webm": "video/webm",
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".m4a": "audio/mp4",
  ".aac": "audio/aac",
  ".ogg": "audio/ogg",
  ".oga": "audio/ogg",
});

function buildNormalizedContentTypeParameters(parameters) {
  return parameters
    .map((parameter) => {
      const trimmedParameter = String(parameter || "").trim();
      if (!trimmedParameter) {
        return null;
      }

      const separatorIndex = trimmedParameter.indexOf("=");
      if (separatorIndex < 0) {
        return trimmedParameter;
      }

      const key = trimmedParameter.slice(0, separatorIndex).trim().toLowerCase();
      const rawValue = trimmedParameter.slice(separatorIndex + 1).trim();
      if (!key) {
        return null;
      }

      if (key !== "codecs") {
        return `${key}=${rawValue}`;
      }

      const codecs = rawValue
        .replace(/^"(.*)"$/u, "$1")
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);

      if (!codecs.length) {
        return null;
      }

      return `codecs="${codecs.join(", ")}"`;
    })
    .filter(Boolean);
}

export function normalizeRecordingMediaContentType(contentType) {
  const normalizedContentType = normalizeOptionalString(contentType);
  if (!normalizedContentType) {
    return null;
  }

  const [baseType, ...parameters] = normalizedContentType
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!baseType) {
    return null;
  }

  const normalizedParameters = buildNormalizedContentTypeParameters(parameters);
  return [baseType.toLowerCase(), ...normalizedParameters].join("; ");
}

export function resolvePlaybackSourceType(
  mediaSource,
  { documentOverride = typeof document !== "undefined" ? document : null } = {},
) {
  const normalizedContentType = normalizeRecordingMediaContentType(
    mediaSource?.contentType,
  );
  const baseContentType = normalizedContentType
    ? normalizedContentType.split(";")[0]
    : null;
  const hasCodecParameters =
    Boolean(normalizedContentType) && normalizedContentType !== baseContentType;

  if (!mediaSource?.kind) {
    return {
      declaredType: null,
      normalizedContentType,
      baseContentType,
      canPlayExact: "",
      canPlayBase: "",
      strategy: "missing",
    };
  }

  if (
    !documentOverride?.createElement ||
    typeof documentOverride.createElement !== "function"
  ) {
    return {
      declaredType: normalizedContentType || baseContentType || null,
      normalizedContentType,
      baseContentType,
      canPlayExact: "",
      canPlayBase: "",
      strategy: normalizedContentType ? "exact" : "omitted",
    };
  }

  const mediaElement = documentOverride.createElement(
    mediaSource.kind === "audio" ? "audio" : "video",
  );
  const canPlayType =
    typeof mediaElement?.canPlayType === "function"
      ? mediaElement.canPlayType.bind(mediaElement)
      : null;
  const canPlayExact = normalizedContentType
    ? canPlayType?.(normalizedContentType) || ""
    : "";
  const canPlayBase = baseContentType ? canPlayType?.(baseContentType) || "" : "";

  // Browser-upload metadata can include codec strings that are more specific
  // than the final playable asset, or simply wrong for how the browser sniffs
  // signed review-media responses. Prefer the safe container type whenever the
  // browser says it can play that container.
  if (baseContentType && canPlayBase && hasCodecParameters) {
    return {
      declaredType: baseContentType,
      normalizedContentType,
      baseContentType,
      canPlayExact,
      canPlayBase,
      strategy: canPlayExact ? "container_preferred" : "container_fallback",
    };
  }

  if (normalizedContentType && canPlayExact) {
    return {
      declaredType: normalizedContentType,
      normalizedContentType,
      baseContentType,
      canPlayExact,
      canPlayBase,
      strategy: "exact",
    };
  }

  if (baseContentType && canPlayBase) {
    return {
      declaredType: baseContentType,
      normalizedContentType,
      baseContentType,
      canPlayExact,
      canPlayBase,
      strategy: normalizedContentType ? "container_fallback" : "container",
    };
  }

  return {
    declaredType: null,
    normalizedContentType,
    baseContentType,
    canPlayExact,
    canPlayBase,
    strategy: normalizedContentType ? "omitted_unsupported" : "omitted",
  };
}

function isManualAudioRecording(variant, measurement) {
  return (
    normalizeOptionalString(variant) === "RecordingManualAudio" ||
    normalizeOptionalString(measurement?.appMode) === "manual-audio"
  );
}

function inferRecordingMediaContentType({
  explicitContentType,
  directUrl,
  storagePath,
}) {
  const normalizedExplicitContentType =
    normalizeOptionalString(explicitContentType);
  if (normalizedExplicitContentType) {
    return normalizedExplicitContentType;
  }

  const pathCandidate = firstNonEmptyString([directUrl, storagePath]);
  if (!pathCandidate) {
    return null;
  }

  let normalizedPath = pathCandidate;
  try {
    normalizedPath = new URL(pathCandidate).pathname;
  } catch {
    normalizedPath = pathCandidate;
  }

  const sanitizedPath = normalizedPath
    .split("?")[0]
    .split("#")[0]
    .trim()
    .toLowerCase();
  const extensionIndex = sanitizedPath.lastIndexOf(".");
  if (extensionIndex < 0) {
    return null;
  }

  return MEDIA_CONTENT_TYPES_BY_EXTENSION[sanitizedPath.slice(extensionIndex)] || null;
}

export function resolveRecordingMediaSource(variant, measurement) {
  const reviewMediaUrl = firstNonEmptyString([
    measurement?.reviewMedia?.url,
    measurement?.reviewMedia?.signedUrl,
  ]);
  const reviewMediaKind = firstNonEmptyString([measurement?.reviewMedia?.kind]);
  if (reviewMediaUrl && reviewMediaKind) {
    return {
      kind: reviewMediaKind,
      directUrl: reviewMediaUrl,
      storagePath: null,
      audioOnly: reviewMediaKind === "audio",
      contentType: inferRecordingMediaContentType({
        explicitContentType: measurement?.reviewMedia?.contentType,
        directUrl: reviewMediaUrl,
      }),
    };
  }

  const videoUrl = firstNonEmptyString([measurement?.videoDownloadURL]);
  const videoPath = firstNonEmptyString([
    measurement?.videoPath,
    measurement?.gcsUri,
  ]);
  const audioUrl = firstNonEmptyString([
    measurement?.audioDownloadURL,
    measurement?.videoDownloadURL,
  ]);
  const audioPath = firstNonEmptyString([
    measurement?.audioPath,
    measurement?.videoPath,
    measurement?.gcsUri,
  ]);
  const audioOnly = isManualAudioRecording(variant, measurement);

  if (audioOnly) {
    return {
      kind: audioUrl || audioPath ? "audio" : null,
      directUrl: audioUrl,
      storagePath: audioPath,
      audioOnly: true,
      contentType: inferRecordingMediaContentType({
        explicitContentType:
          measurement?.mimeType || measurement?.recordingMeta?.mimeType,
        directUrl: audioUrl,
        storagePath: audioPath,
      }),
    };
  }

  if (videoUrl || videoPath) {
    return {
      kind: "video",
      directUrl: videoUrl,
      storagePath: videoPath,
      audioOnly: false,
      contentType: inferRecordingMediaContentType({
        explicitContentType:
          measurement?.mimeType || measurement?.recordingMeta?.mimeType,
        directUrl: videoUrl,
        storagePath: videoPath,
      }),
    };
  }

  if (audioUrl || audioPath) {
    return {
      kind: "audio",
      directUrl: audioUrl,
      storagePath: audioPath,
      audioOnly: false,
      contentType: inferRecordingMediaContentType({
        explicitContentType:
          measurement?.mimeType || measurement?.recordingMeta?.mimeType,
        directUrl: audioUrl,
        storagePath: audioPath,
      }),
    };
  }

  return {
    kind: null,
    directUrl: null,
    storagePath: null,
    audioOnly,
    contentType: null,
  };
}

export async function resolveRecordingMediaUrl(mediaSource) {
  if (mediaSource?.directUrl) {
    return mediaSource.directUrl;
  }

  if (!mediaSource?.storagePath) {
    return null;
  }

  const [{ getDownloadURL, getStorage, ref: storageRef }, { firebaseApp }] =
    await Promise.all([
      import("firebase/storage"),
      import("../../../firebase/firebaseApp"),
    ]);
  const storage = getStorage(firebaseApp);
  return getDownloadURL(storageRef(storage, mediaSource.storagePath));
}
